import React, { useEffect, useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import ArticleCard from '../components/ArticleCard';

const API_URL = 'https://api.sifgroup.org/api/v1/articles?limit=20&offset=0';

const formatDate = (value) => {
  if (!value) return 'Recently';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Recently';
  }

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }).format(date);
};

const Articles = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await fetch(API_URL);
        if (!response.ok) {
          throw new Error('Failed to fetch articles');
        }

        const data = await response.json();
        const publishedArticles = Array.isArray(data)
          ? data.filter((article) => article.status === 'published')
          : [];

        const latestArticles = [...publishedArticles]
          .sort((a, b) => {
            const aDate = new Date(a.published_at || a.created_at || 0).getTime();
            const bDate = new Date(b.published_at || b.created_at || 0).getTime();
            return bDate - aDate;
          })
          .slice(0, 12);

        setArticles(latestArticles);
      } catch (error) {
        console.error('Error fetching articles:', error);
        setArticles([]);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  return (
    <>
      <section className="blog-hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-pattern"></div>
        <Container className="position-relative z-3 h-100">
          <Row className="h-100 align-items-center justify-content-center">
            <Col xs={12} lg={10} xl={8} className="text-center text-white">
              <div className="hero-content-wrapper">
                <h1 className="hero-title mb-4">Golf Articles</h1>
                <p className="hero-subtitle mb-5">
                  Expert articles, tips, and news to help you improve your game and stay updated with the golf community
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section id="featured" className="section-padding">
        <Container>
          <Row className="mb-5">
            <Col xs={12} className="text-center">
              <h2 className="section-title mb-3">Latest Articles</h2>
              <p className="section-subtitle mb-0">
                Fresh golf insights, expert advice, and practical ideas from our latest content
              </p>
            </Col>
          </Row>

          {loading ? (
            <Row className="g-4 justify-content-center">
              <Col xs={12} className="text-center">
                <p className="text-muted">Loading articles...</p>
              </Col>
            </Row>
          ) : articles.length > 0 ? (
            <Row className="g-4 justify-content-center">
              {articles.map((post) => (
                <Col xs={12} md={6} lg={4} key={post.id}>
                  <ArticleCard
                    id={post.id}
                    title={post.title}
                    excerpt={post.excerpt || post.seo_description || 'Read the latest golf insights and expert advice.'}
                    imageUrl={post.featured_image || 'https://images.unsplash.com/photo-1535131749006-b7f58c99034b'}
                    date={formatDate(post.published_at || post.created_at)}
                    readTime={post.read_time || 5}
                    category={post.topic}
                  />
                </Col>
              ))}
            </Row>
          ) : (
            <Row>
              <Col xs={12} className="text-center py-5">
                <h3 className="h4 mb-3 text-green">No articles available</h3>
                <p className="text-muted mb-0">Check back soon for new content.</p>
              </Col>
            </Row>
          )}
        </Container>
      </section>

      {/*
      All Blog Posts
      <section id="all-posts" className="section-padding bg-soft-sage">
        <Container>
          <Row className="mb-5 align-items-end">
            <Col xs={12} className="text-center mb-4">
              <h2 className="section-title mb-3">
                {selectedCategory === "All" ? "All Articles" : `${selectedCategory} Articles`}
              </h2>
              <p className="section-subtitle mb-4 mb-md-0">
                {selectedCategory === "All"
                  ? "Browse our complete collection of golf articles and insights"
                  : `Articles related to ${selectedCategory}`
                }
              </p>
            </Col>
            <Col xs={12} className="text-center">
              <div className="d-flex align-items-center justify-content-center">
                <span className="me-2 text-muted">Showing:</span>
                <span className="fw-bold text-gold">
                  {filteredPosts.length} {filteredPosts.length === 1 ? 'article' : 'articles'}
                </span>
              </div>
            </Col>
          </Row>

          {filteredPosts.length > 0 ? (
            <Row className="g-4 justify-content-center">
              {filteredPosts.map((post) => (
                <Col xs={12} sm={6} lg={4} key={post.id}>
                  <div className="fade-in">
                    <BlogCard {...post} />
                  </div>
                </Col>
              ))}
            </Row>
          ) : (
            <Row>
              <Col xs={12} className="text-center py-5">
                <div className="empty-state-icon mb-4">
                  🏌️
                </div>
                <h3 className="h4 mb-3 text-green">
                  No articles found in this category
                </h3>
                <p className="text-muted mb-4">
                  Try selecting a different category or check back soon for new content
                </p>
                <button
                  className="btn-gold"
                  onClick={() => setSelectedCategory("All")}
                >
                  View All Articles
                </button>
              </Col>
            </Row>
          )}

          Category Stats
          <Row className="mt-5 pt-5 border-top">
            <Col xs={12} className="text-center">
              <div className="d-flex flex-wrap justify-content-center gap-4">
                <div className="stat-item">
                  <h3 className="stat-number mb-1">{blogPosts.length}</h3>
                  <p className="stat-label">Total Articles</p>
                </div>
                <div className="stat-item">
                  <h3 className="stat-number mb-1">{getAllCategories().length}</h3>
                  <p className="stat-label">Categories</p>
                </div>
                <div className="stat-item">
                  <h3 className="stat-number mb-1">6+</h3>
                  <p className="stat-label">Expert Authors</p>
                </div>
                <div className="stat-item">
                  <h3 className="stat-number mb-1">98%</h3>
                  <p className="stat-label">Reader Satisfaction</p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
      */}

      {/* Popular Categories section removed as requested. */}

      {/* CSS Styles */}
      <style>{`
        /* Hero Section with Adjusted Spacing for Mobile */
        .blog-hero-section {
          height: 70vh;
          min-height: 600px;
          background: linear-gradient(rgba(31, 61, 43, 0.6), rgba(31, 61, 43, 0.75)), 
                     url('https://static.vecteezy.com/system/resources/thumbnails/070/232/188/small/a-golf-ball-rests-on-the-edge-of-a-golf-hole-on-a-green-course-at-sunset-with-an-orange-sky-free-photo.jpg') center/cover no-repeat;
          position: relative;
          overflow: hidden;
          display: flex;
          align-items: center;
          padding-top: 80px; /* Added base padding for mobile */
        }
        
        .hero-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(180deg, rgba(31, 61, 43, 0.5) 0%, rgba(31, 61, 43, 0.7) 100%);
          z-index: 1;
        }
        
        .hero-pattern {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: radial-gradient(circle at center, rgba(181, 154, 90, 0.1) 0%, transparent 70%);
          animation: pulse 4s ease-in-out infinite;
          z-index: 1;
        }
        
        .hero-content-wrapper {
          position: relative;
          z-index: 2;
          width: 100%;
        }
        
        .hero-title {
          font-size: clamp(2rem, 5vw, 3.5rem);
          color: #B59A5A;
          font-weight: 600;
          text-shadow: 0 2px 4px rgba(0,0,0,0.3);
          line-height: 1.2;
          margin-bottom: 1.5rem;
          font-family: 'Playfair Display', serif;
        }
        
        .hero-subtitle {
          font-size: clamp(1rem, 2.5vw, 1.5rem);
          line-height: 1.6;
          color: rgba(255, 255, 255, 0.95);
          text-shadow: 0 1px 3px rgba(0,0,0,0.3);
          margin-bottom: 2.5rem;
          max-width: 800px;
          margin-left: auto;
          margin-right: auto;
          font-weight: 400;
        }
        
        .hero-cta {
          position: relative;
          z-index: 2;
        }
        
        /* Section Styles */
        .section-padding {
          padding: clamp(3rem, 6vw, 5rem) 0;
        }
        
        .section-padding-sm {
          padding: clamp(1.5rem, 3vw, 3rem) 0;
        }
        
        .section-title {
          font-size: clamp(1.75rem, 4vw, 2.5rem);
          color: #1F3D2B;
          font-weight: 600;
          margin-bottom: 1rem;
          line-height: 1.2;
          font-family: 'Playfair Display', serif;
        }
        
        .section-subtitle {
          color: #666;
          font-size: clamp(0.9rem, 1.5vw, 1.1rem);
          line-height: 1.6;
          max-width: 700px;
          margin-left: auto;
          margin-right: auto;
          font-weight: 400;
        }
        
        /* Text Colors */
        .text-gold {
          color: #B59A5A;
        }
        
        .text-green {
          color: #1F3D2B;
        }
        
        .text-muted {
          color: #666;
        }
        
        /* Empty State */
        .empty-state-icon {
          font-size: clamp(3rem, 8vw, 5rem);
          color: #e3ece6;
          margin-bottom: 1.5rem;
        }
        
        /* Stats */
        .stat-item {
          padding: 0 1rem;
          margin-bottom: 1.5rem;
        }
        
        .stat-number {
          font-size: clamp(1.75rem, 4vw, 2.5rem);
          color: #B59A5A;
          font-weight: 600;
          margin-bottom: 0.5rem;
          font-family: 'Playfair Display', serif;
        }
        
        .stat-label {
          color: #666;
          font-size: clamp(0.8rem, 1.5vw, 0.9rem);
          margin-bottom: 0;
          font-weight: 400;
        }
        
        /* Newsletter Card */
        .newsletter-card {
          background: linear-gradient(135deg, #1F3D2B 0%, #2a4d3b 100%);
          color: white;
          padding: clamp(2rem, 4vw, 3rem);
          border-radius: 8px;
          width: 100%;
        }
        
        .newsletter-icon {
          width: clamp(60px, 10vw, 80px);
          height: clamp(60px, 10vw, 80px);
          background-color: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: clamp(1.5rem, 3vw, 2rem);
          margin: 0 auto 1.5rem;
        }
        
        .newsletter-text {
          opacity: 0.9;
          font-size: clamp(0.9rem, 1.5vw, 1.1rem);
          line-height: 1.6;
          margin-bottom: 2rem;
          font-weight: 400;
        }
        
        .newsletter-terms {
          opacity: 0.7;
          font-size: clamp(0.75rem, 1.2vw, 0.85rem);
          font-weight: 300;
        }
        
        /* Newsletter Form */
        .newsletter-input-wrapper {
          max-width: 500px;
          margin: 0 auto;
          display: flex;
          flex-direction: row;
          gap: 0;
          border-radius: 4px;
          overflow: hidden;
          box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        }
        
        .newsletter-email-input {
          flex: 1;
          padding: clamp(12px, 2vw, 15px) clamp(15px, 2vw, 20px);
          border: none;
          font-size: clamp(0.9rem, 1.5vw, 1rem);
          background: #ffffff;
          color: #333333;
          min-width: 0;
          outline: none;
          font-family: inherit;
          line-height: 1.5;
          font-weight: 400;
        }
        
        .newsletter-email-input::placeholder {
          color: #666666 !important;
          opacity: 0.8 !important;
          font-weight: 400;
        }
        
        .newsletter-email-input::-webkit-input-placeholder {
          color: #666666 !important;
          opacity: 0.8 !important;
        }
        
        .newsletter-email-input::-moz-placeholder {
          color: #666666 !important;
          opacity: 0.8 !important;
        }
        
        .newsletter-email-input:-ms-input-placeholder {
          color: #666666 !important;
          opacity: 0.8 !important;
        }
        
        .newsletter-email-input:-moz-placeholder {
          color: #666666 !important;
          opacity: 0.8 !important;
        }
        
        .newsletter-email-input:focus {
          background: #ffffff;
          box-shadow: none;
        }
        
        .newsletter-subscribe-btn {
          border: none;
          padding: clamp(12px, 2vw, 15px) clamp(20px, 3vw, 32px);
          font-weight: 600;
          font-size: clamp(0.9rem, 1.5vw, 1rem);
          white-space: nowrap;
          background-color: #B59A5A;
          color: #FFFFFF;
          transition: all 0.3s ease;
          cursor: pointer;
          text-shadow: 0 1px 2px rgba(0,0,0,0.2);
          font-family: inherit;
          line-height: 1.5;
          text-align: center;
          text-decoration: none;
          user-select: none;
          display: inline-block;
          border-left: 1px solid rgba(0,0,0,0.1);
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        
        .newsletter-subscribe-btn:hover {
          background-color: #d4b56e;
          color: #FFFFFF;
        }
        
        /* Category Cards */
        .category-card {
          padding: clamp(1.5rem, 3vw, 2rem);
          text-align: center;
          transition: all 0.3s ease;
          height: 100%;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 200px;
          border: none;
          outline: none;
        }
        
        .category-card:hover {
          transform: translateY(-5px);
        }
        
        .category-card:focus {
          outline: 2px solid #B59A5A;
          outline-offset: 2px;
        }
        
        .category-icon {
          width: clamp(50px, 8vw, 60px);
          height: clamp(50px, 8vw, 60px);
          background-color: #f0f7f3;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: clamp(1.25rem, 2vw, 1.5rem);
          margin-bottom: 1rem;
        }
        
        .category-title {
          color: #1F3D2B;
          font-size: clamp(1rem, 1.8vw, 1.25rem);
          font-weight: 600;
          margin-bottom: 0.5rem;
          line-height: 1.3;
          font-family: 'Playfair Display', serif;
        }
        
        .category-count {
          color: #666;
          font-size: clamp(0.8rem, 1.2vw, 0.9rem);
          margin-bottom: 0;
          font-weight: 400;
        }
        
        /* Background Colors */
        .bg-soft-sage {
          background-color: #f0f7f3;
        }
        
        .bg-primary-green {
          background-color: #1F3D2B;
        }
        
        /* Animations */
        @keyframes pulse {
          0%, 100% {
            opacity: 0.1;
          }
          50% {
            opacity: 0.2;
          }
        }
        
        /* Responsive Button Sizes */
        .btn-lg {
          padding: clamp(10px, 2vw, 12px) clamp(20px, 3vw, 32px);
          font-size: clamp(0.9rem, 1.5vw, 1rem);
        }
        
        /* Responsive Grid Gaps */
        .g-4 {
          --bs-gutter-x: clamp(1rem, 2vw, 1.5rem);
          --bs-gutter-y: clamp(1rem, 2vw, 1.5rem);
        }
        
        /* ================= MOBILE RESPONSIVE FIXES ================= */
        
        /* Extra Small Devices - Mobile Phones */
        @media (max-width: 576px) {
          .blog-hero-section {
            height: 65vh; /* Increased height for mobile */
            min-height: 500px;
            padding-top: 100px; /* Significantly increased top padding for mobile */
            display: flex;
            align-items: center;
          }
          
          .hero-content-wrapper {
            margin-top: 20px; /* Additional margin for mobile */
          }
          
          .hero-title {
            margin-bottom: 1rem;
            font-size: 2.2rem;
          }
          
          .hero-subtitle {
            margin-bottom: 2rem;
            font-size: 1.1rem;
          }
          
          .hero-cta {
            flex-direction: column;
            gap: 1rem;
            margin-top: 20px;
          }
          
          .hero-cta .btn {
            width: 100%;
            padding: 14px 20px;
            font-size: 1rem;
          }
          
          .category-card {
            min-height: 180px;
          }
          
          .stat-item {
            padding: 0 0.5rem;
          }
          
          /* Newsletter form mobile */
          .newsletter-input-wrapper {
            flex-direction: column;
            gap: 1rem;
            border-radius: 4px;
            overflow: visible;
            box-shadow: none;
            max-width: 100%;
          }
          
          .newsletter-email-input {
            width: 100%;
            border-radius: 4px;
            padding: 14px 20px;
            font-size: 1rem;
            background: #ffffff !important;
            border: 1px solid rgba(255, 255, 255, 0.2);
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            color: #333333 !important;
          }
          
          .newsletter-email-input::placeholder {
            color: #666666 !important;
            opacity: 0.8 !important;
            font-size: 0.95rem;
            font-weight: 400;
          }
          
          .newsletter-subscribe-btn {
            width: 100%;
            border-radius: 4px;
            padding: 14px 20px;
            font-size: 1rem;
            border-left: none;
            box-shadow: 0 4px 12px rgba(181, 154, 90, 0.2);
          }
          
          .btn-gold, .btn-gold-filled {
            padding: 12px 24px;
            font-size: 0.95rem;
          }
        }
        
        /* Small Devices - Larger Mobile Phones */
        @media (min-width: 577px) and (max-width: 768px) {
          .blog-hero-section {
            height: 70vh;
            min-height: 550px;
            padding-top: 90px; /* Increased padding for small devices */
          }
          
          .hero-title {
            font-size: 2.8rem;
          }
          
          .hero-subtitle {
            font-size: 1.3rem;
          }
          
          .hero-cta {
            margin-top: 10px;
          }
          
          .newsletter-input-wrapper {
            max-width: 450px;
          }
          
          .newsletter-email-input::placeholder {
            color: #666666 !important;
            opacity: 0.8 !important;
          }
        }
        
        /* Medium Devices - Tablets */
        @media (min-width: 769px) and (max-width: 992px) {
          .blog-hero-section {
            height: 75vh;
            min-height: 600px;
            padding-top: 80px; /* Normal padding for tablets */
          }
          
          .hero-content-wrapper {
            margin-top: 0;
          }
          
          .newsletter-input-wrapper {
            max-width: 500px;
          }
          
          .newsletter-subscribe-btn {
            padding: 12px 24px;
          }
          
          .newsletter-email-input::placeholder {
            color: #666666 !important;
            opacity: 0.8 !important;
          }
        }
        
        /* Large Devices - Desktops */
        @media (min-width: 993px) and (max-width: 1199px) {
          .blog-hero-section {
            height: 80vh;
            min-height: 650px;
            padding-top: 60px; /* Reduced padding for desktop */
          }
          
          .newsletter-subscribe-btn {
            padding: 12px 32px;
          }
          
          .newsletter-email-input::placeholder {
            color: #666666 !important;
            opacity: 0.8 !important;
          }
        }
        
        /* Very Large Screens */
        @media (min-width: 1200px) {
          .blog-hero-section {
            height: 85vh;
            min-height: 700px;
            padding-top: 40px; /* Minimal padding for very large screens */
          }
          
          .newsletter-input-wrapper {
            max-width: 550px;
          }
          
          .newsletter-subscribe-btn {
            padding: 12px 32px;
            font-size: 1rem;
          }
          
          .newsletter-email-input::placeholder {
            color: #666666 !important;
            opacity: 0.8 !important;
          }
        }
        
        /* Landscape Orientation on Mobile */
        @media (max-height: 600px) and (orientation: landscape) {
          .blog-hero-section {
            height: 100vh;
            min-height: 400px;
            padding-top: 80px; /* Reduced for landscape */
          }
          
          .hero-title {
            font-size: 2rem;
            margin-bottom: 0.5rem;
          }
          
          .hero-subtitle {
            font-size: 1rem;
            margin-bottom: 1.5rem;
          }
          
          .hero-content-wrapper {
            margin-top: 10px;
          }
          
          .newsletter-card {
            padding: 1.5rem;
          }
          
          .newsletter-subscribe-btn {
            padding: 10px 20px;
            font-size: 0.9rem;
          }
          
          .newsletter-email-input::placeholder {
            color: #666666 !important;
            opacity: 0.8 !important;
          }
        }
        
        /* Short Height Screens */
        @media (max-height: 700px) and (max-width: 576px) {
          .blog-hero-section {
            min-height: 450px;
            padding-top: 80px;
          }
          
          .hero-content-wrapper {
            margin-top: 10px;
          }
        }
        
        /* Accessibility: Focus states */
        button:focus,
        input:focus,
        .category-card:focus {
          outline: 2px solid #B59A5A;
          outline-offset: 2px;
        }
        
        /* Print styles */
        @media print {
          .hero-cta,
          .newsletter-card,
          .category-card {
            display: none !important;
          }
          
          .blog-hero-section {
            padding-top: 0;
            height: auto;
            min-height: auto;
          }
        }
      `}</style>
    </>
  );
};

export default Articles;
